import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring, SpringValue } from 'react-spring';

import { SPRINGS } from '@/constants';
import { generateId, random } from '@/utils';
import useBoopMinimal from '@/hooks/use-boop-minimal';
import useInterval from '@/hooks/use-interval';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import VisuallyHidden from '@/components/VisuallyHidden';

import Svg from './Svg';

export type Status = 'idle' | 'hovering' | 'pressed' | 'formatting';

interface Props {
  size?: number;
  status: Status;
  timing?: number;
  wandFillColor?: string;
  accentColor?: string;
  style?: React.CSSProperties;
}

interface Sparkle {
  id: string;
  createdAt: number;
}

const FormatIcon = ({
  size = 16,
  status,
  timing = 150,
  wandFillColor = 'var(--color-gray-100)',
  accentColor,
  style = {},
  ...delegated
}: Props) => {
  const isHovering = status === 'hovering';
  const isBooped = useBoopMinimal(isHovering, timing);
  const [sparkleIds, setSparkleIds] = React.useState<Array<Sparkle>>(
    []
  );

  const prefersReducedMotion = usePrefersReducedMotion();

  const intervalCallback = React.useCallback(() => {
    // Clean up old sparkles
    setSparkleIds((currentSparkles) => {
      const now = Date.now();

      return currentSparkles.filter(({ createdAt }) => {
        const ageInSeconds = (now - createdAt) / 1000;

        return ageInSeconds < 10;
      });
    });
  }, []);
  useInterval(intervalCallback, 10_000);

  React.useEffect(() => {
    if (status !== 'formatting') {
      return;
    }

    const newSparkle = {
      id: generateId(8),
      createdAt: Date.now(),
    };

    setSparkleIds((currentIds) => [...currentIds, newSparkle]);
  }, [status]);

  const wandRotation = getWandRotation(status, isBooped);

  const wandGroupStyle = useSpring({
    transform: `rotate(${wandRotation}deg)`,
    config: SPRINGS.springy,
    immediate: prefersReducedMotion,
  });

  return (
    <>
      <Svg
        viewBox="0 0 24 24"
        fill="none"
        style={{
          width: size,
          height: size,
          opacity: status !== 'idle' ? 1 : 0.7,
          overflow: 'visible',
          ...style,
        }}
        {...delegated}
      >
        {sparkleIds.map((newSparkle) => (
          <SparkleGroup
            key={newSparkle.id}
            accentColor={accentColor}
            prefersReducedMotion={prefersReducedMotion}
          />
        ))}

        <Wand style={wandGroupStyle}>
          <rect
            x={0}
            y={8}
            width={24}
            height={6}
            rx={2}
            fill={wandFillColor}
          />
          <line x1={18} y1={8} x2={18} y2={14} />
        </Wand>
      </Svg>
      <VisuallyHidden>Format code using Prettier</VisuallyHidden>
    </>
  );
};

const SparkleGroup = React.memo(function SparkleGroup({
  accentColor,
  prefersReducedMotion,
}: {
  accentColor?: string;
  prefersReducedMotion: boolean;
}) {
  // Each spring should have subtle variations, so that each
  // click produces a slightly different result.
  //
  // No need for state/refs, since this component should not
  // re-render during its animation.
  const firstTranslateX = random(4, 20);
  const firstTranslateY = random(-10, 0);
  const firstRotation = random(-90, -220);
  const firstScale = random(1.25, 1.75, { rounded: false });

  const secondTranslateX = random(4, 8);
  const secondTranslateY = random(9, 20);
  const secondRotation = random(-200, -250);
  const secondScale = random(1, 1.5, { rounded: false });

  const thirdTranslateX = random(-15, -4);
  const thirdTranslateY = random(-12, 0);
  const thirdRotation = random(-90, -220);
  const thirdScale = random(1.25, 1.75, { rounded: false });

  const firstSparkleSpring = useSpring({
    from: {
      transform: 'translate(0px, 0px) rotate(0deg) scale(1)',
    },
    to: {
      transform: `translate(${firstTranslateX}px, ${firstTranslateY}px) rotate(${firstRotation}deg) scale(${firstScale})`,
    },
    config: {
      tension: 200,
      friction: 40,
    },
    delay: 0,
    immediate: prefersReducedMotion,
  });

  const secondSparkleSpring = useSpring({
    from: {
      transform: 'translate(0px, 0px) rotate(0deg) scale(1)',
    },
    to: {
      transform: `translate(${secondTranslateX}px, ${secondTranslateY}px) rotate(${secondRotation}deg) scale(${secondScale})`,
    },
    config: {
      tension: 80,
      friction: 14,
    },
    delay: 50,
    immediate: prefersReducedMotion,
  });

  const thirdSparkleSpring = useSpring({
    from: {
      transform: 'translate(0px, 0px) rotate(0deg) scale(1)',
    },
    to: {
      transform: `translate(${thirdTranslateX}px, ${thirdTranslateY}px) rotate(${thirdRotation}deg) scale(${thirdScale})`,
    },
    config: {
      tension: 100,
      friction: 20,
    },
    delay: 140,
    immediate: prefersReducedMotion,
  });

  return (
    <>
      <Sparkle
        springData={thirdSparkleSpring}
        accentColor={accentColor}
        fadeDuration={1600}
        fadeDelay={700}
      />
      <Sparkle
        springData={secondSparkleSpring}
        accentColor={accentColor}
        fadeDuration={1000}
        fadeDelay={500}
      />
      <Sparkle
        springData={firstSparkleSpring}
        accentColor={accentColor}
        fadeDuration={900}
        fadeDelay={350}
      />
    </>
  );
});

function Sparkle({
  springData,
  accentColor,
  fadeDuration,
  fadeDelay,
}: {
  springData: {
    transform: SpringValue<string>;
  };
  accentColor?: string;
  fadeDuration: number;
  fadeDelay: number;
}) {
  return (
    <SparkleWrapper
      style={{
        '--animation-duration': fadeDuration + 'ms',
        '--animation-delay': fadeDelay + 'ms',
      }}
    >
      <StarGroup stroke={accentColor} style={springData}>
        <polygon points="16 4, 20 4" />
        <polygon points="18 2, 18 6" />
      </StarGroup>
    </SparkleWrapper>
  );
}

function getWandRotation(status: Status, isBooped: boolean) {
  if (isBooped) {
    return -35;
  }
  switch (status) {
    case 'idle':
    case 'hovering':
      return -45;
    case 'pressed':
      return -65;
    case 'formatting':
      return -45;
  }
}

const SparkleWrapper = styled.g`
  animation: fadeToTransparent var(--animation-duration) both;
  animation-delay: var(--animation-delay);
`;

const Wand = styled(animated.g)`
  transform-origin: 12px 12px;
`;
const StarGroup = styled(animated.g)`
  transform-origin: 18px 4px;
`;

export default FormatIcon;
